import { resolveDashboardReturnUrl } from '../utils/catalog-item-ids.util';
import {
  DEFAULT_WEB_CABINET_PATHS,
  normalizeCabinetPath,
  type CabinetPaths,
} from '@nebalkon-workspace/cabinet-core';

export interface ClientCabinetNavItem {
  labelKey: string;
  route: string;
  exact?: boolean;
  icon: string;
}

/** Bottom tab navigation for the client cabinet shell. */
export const CLIENT_CABINET_BOTTOM_TABS: ClientCabinetNavItem[] = [
  {
    labelKey: 'CLIENT.CABINET.NAV.HOME',
    route: '',
    exact: true,
    icon: '/icons/tab-home.svg',
  },
  {
    labelKey: 'CLIENT.CABINET.NAV.MY_STORAGE',
    route: 'my-storage',
    icon: '/icons/tab-storage.svg',
  },
  {
    labelKey: 'CLIENT.CABINET.NAV.WALLET',
    route: 'wallet',
    icon: '/icons/tab-wallet.svg',
  },
  {
    labelKey: 'CLIENT.CABINET.NAV.PROFILE',
    route: 'profile',
    icon: '/icons/tab-profile.svg',
  },
  {
    labelKey: 'CLIENT.CABINET.NAV.HELP',
    route: 'help',
    icon: '/icons/tab-help.svg',
  },
];

const TAB_ROOT_SUFFIXES = ['', '/my-storage', '/wallet', '/help', '/profile'] as const;

const BOTTOM_TABS_HIDDEN_SUFFIXES = [
  '/my-storage/request',
  '/my-storage/delivery',
  '/requests/storage/create',
] as const;

export function normalizeClientCabinetPath(url: string): string {
  return normalizeCabinetPath(url);
}

export function clientCabinetNavLink(
  route: string,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
): string {
  if (!route) {
    return paths.client;
  }

  return `${paths.client}/${route}`;
}

export function isClientCabinetNavActive(
  currentUrl: string,
  item: ClientCabinetNavItem,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
): boolean {
  const normalized = normalizeClientCabinetPath(currentUrl);
  const link = clientCabinetNavLink(item.route, paths);

  if (item.route === 'profile') {
    return (
      normalized === link ||
      normalized.startsWith(`${link}/`) ||
      normalized.startsWith(`${paths.clientRequests}`)
    );
  }

  if (item.exact) {
    return normalized === link;
  }

  return normalized === link || normalized.startsWith(`${link}/`);
}

export function isClientCabinetTabRoot(
  path: string,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
): boolean {
  const normalized = normalizeClientCabinetPath(path);

  return TAB_ROOT_SUFFIXES.some(
    (suffix) => normalized === `${paths.client}${suffix}`,
  );
}

/** Routes where page chrome shows toolbar only (no h1/icon header row). */
export function shouldHideClientCabinetPageHeader(
  path: string,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
): boolean {
  if (isClientCabinetTabRoot(path, paths)) {
    return true;
  }

  const normalized = normalizeClientCabinetPath(path);
  return normalized === `${paths.client}/requests`;
}

export function resolveClientCabinetBackRoute(
  path: string,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
): string {
  const normalized = normalizeClientCabinetPath(path);
  const client = paths.client;

  if (normalized.startsWith(`${client}/profile`)) {
    if (normalized === `${client}/profile`) {
      return client;
    }

    return `${client}/profile`;
  }

  if (normalized.startsWith(`${client}/my-storage`)) {
    return `${client}/my-storage`;
  }

  if (normalized.startsWith(`${client}/requests`)) {
    if (normalized === `${client}/requests`) {
      return `${client}/profile`;
    }

    return `${client}/requests`;
  }

  if (normalized.startsWith(`${client}/help`)) {
    return `${client}/help`;
  }

  if (normalized.startsWith(`${client}/wallet`)) {
    return `${client}/wallet`;
  }

  if (normalized.startsWith(`${client}/tariffs`)) {
    return client;
  }

  if (normalized.startsWith(`${client}/tasks`)) {
    return client;
  }

  return client;
}

export function resolveClientCabinetBackRouteFromUrl(
  url: string,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
): string {
  const queryIndex = url.indexOf('?');
  if (queryIndex >= 0) {
    const returnUrl = resolveDashboardReturnUrl(
      new URLSearchParams(url.slice(queryIndex + 1)).get('returnUrl'),
    );
    if (returnUrl) {
      return returnUrl;
    }
  }

  return resolveClientCabinetBackRoute(url, paths);
}

export function shouldShowClientCabinetBottomTabs(path: string): boolean {
  const normalized = normalizeClientCabinetPath(path);

  if (BOTTOM_TABS_HIDDEN_SUFFIXES.some((suffix) => normalized.endsWith(suffix))) {
    return false;
  }

  return true;
}

export function shouldHideClientCabinetBottomTabs(path: string): boolean {
  return !shouldShowClientCabinetBottomTabs(path);
}

export type ClientCabinetMobileTabItem =
  | {
      type: 'nav';
      labelKey: string;
      route: string;
      icon: string;
      navItem: ClientCabinetNavItem;
    }
  | { type: 'qr'; icon: string };

const CLIENT_CABINET_MOBILE_QR_TAB: ClientCabinetMobileTabItem = {
  type: 'qr',
  icon: '/icons/core/scan-qr.svg',
};

/** Mobile bottom tabs: shared nav items + native QR slot before wallet. */
export function buildClientCabinetMobileBottomTabs(
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
): ClientCabinetMobileTabItem[] {
  const items: ClientCabinetMobileTabItem[] = [];

  for (const tab of CLIENT_CABINET_BOTTOM_TABS) {
    if (tab.route === 'wallet') {
      items.push(CLIENT_CABINET_MOBILE_QR_TAB);
    }

    items.push({
      type: 'nav',
      labelKey: tab.labelKey,
      route: clientCabinetNavLink(tab.route, paths),
      icon: tab.icon,
      navItem: tab,
    });
  }

  return items;
}
